import { BottomCenteredDrawer } from "@jugl-web/ui-components/web/BottomCenteredDrawer";
import { useTranslations } from "@jugl-web/utils";
import {
  useRestApiProvider,
  type CustomTemplateData,
} from "@jugl-web/rest-api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CXAutomationApiSetupSection } from "./components/CXAutomationApiSetupSection";
import { CXAutomationGeneralSection } from "./components/CXAutomationGeneralSection";
import { CXAutomationTestResponseSection } from "./components/CXAutomationTestResponseSection";
import { ReactComponent as ActionsIcon } from "./assets/actions.svg";
import { ReactComponent as APIIcon } from "./assets/api.svg";
import { CXAutomationFormState, createEmptyAutomationFormState } from "./types";
import { deserializeCustomTemplateToAutomation } from "./utils/deserializeCustomTemplateToAutomation";
import {
  isAutomationFormValid,
  serializeAutomationToCustomTemplate,
} from "./utils/serializeAutomationToCustomTemplate";
import { buildTestRequest } from "./utils/buildTestRequest";

type SectionId = "general" | "apiSetup" | "testResponse";

// The backend doesn't cap the test-call response, so guard the UI from
// rendering a multi-MB/GB blob.
const MAX_TEST_RESPONSE_LENGTH = 50_000;

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  entityId: string;
  initialAutomation?: CustomTemplateData;
  onSave: (data: CustomTemplateData) => void;
  isSaving?: boolean;
};

const buildInitialState = (
  initialAutomation: CustomTemplateData | undefined
): CXAutomationFormState =>
  initialAutomation
    ? deserializeCustomTemplateToAutomation(initialAutomation)
    : createEmptyAutomationFormState();

export const CXAutomationDrawer: React.FC<Props> = ({
  isOpen,
  onRequestClose,
  entityId,
  initialAutomation,
  onSave,
  isSaving = false,
}) => {
  const { t } = useTranslations();
  const { integrationsApi } = useRestApiProvider();
  const [executeAgentCurl, { isLoading: isTestRunning }] =
    integrationsApi.useExecuteAgentCurlMutation();

  const [state, setState] = useState<CXAutomationFormState>(() =>
    buildInitialState(initialAutomation)
  );
  const [openSection, setOpenSection] = useState<SectionId | null>("general");
  const [tokenValues, setTokenValues] = useState<Record<string, string>>({});
  const [testResponse, setTestResponse] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setState(buildInitialState(initialAutomation));
      setOpenSection("general");
      setTokenValues({});
      setTestResponse(null);
    }
  }, [isOpen, initialAutomation]);

  const patch = useCallback((next: Partial<CXAutomationFormState>) => {
    setState((prev) => ({ ...prev, ...next }));
  }, []);

  const handleSubmit = useCallback(() => {
    onSave(
      serializeAutomationToCustomTemplate(
        state,
        initialAutomation?.enabled ?? true
      )
    );
  }, [initialAutomation, onSave, state]);

  const handleTest = useCallback(async () => {
    const req = buildTestRequest(state, tokenValues);
    const toDisplayString = (value: unknown): string => {
      let raw: string;
      if (typeof value === "string") {
        raw = value;
      } else {
        try {
          raw = JSON.stringify(value, null, 2) ?? String(value);
        } catch {
          raw = String(value);
        }
      }
      if (raw.length <= MAX_TEST_RESPONSE_LENGTH) return raw;
      return `${raw.slice(0, MAX_TEST_RESPONSE_LENGTH)}\n\n${t(
        {
          id: "cx-actions-page.test-response-truncated",
          defaultMessage:
            "… response truncated — showing first {shown} of {total} characters",
        },
        {
          shown: MAX_TEST_RESPONSE_LENGTH.toLocaleString(),
          total: raw.length.toLocaleString(),
        }
      )}`;
    };
    try {
      const response = await executeAgentCurl({ entityId, ...req }).unwrap();
      setTestResponse(toDisplayString(response));
    } catch {
      setTestResponse(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        })
      );
    }
  }, [entityId, executeAgentCurl, state, tokenValues, t]);

  const makeOpenChangeHandler = useCallback(
    (section: SectionId) => (next: boolean) =>
      setOpenSection(next ? section : null),
    []
  );

  const generalOpenChange = useMemo(
    () => makeOpenChangeHandler("general"),
    [makeOpenChangeHandler]
  );
  const apiSetupOpenChange = useMemo(
    () => makeOpenChangeHandler("apiSetup"),
    [makeOpenChangeHandler]
  );
  const testResponseOpenChange = useMemo(
    () => makeOpenChangeHandler("testResponse"),
    [makeOpenChangeHandler]
  );

  const isSaveDisabled = !isAutomationFormValid(state) || isSaving;
  const isTestDisabled = !state.urlMarkdown.trim() || isTestRunning;

  return (
    <BottomCenteredDrawer
      isOpen={isOpen}
      onClose={onRequestClose}
      size="fullscreen"
      header={{
        type: "title",
        title: (
          <div className="flex items-center gap-3">
            <ActionsIcon />
            <span>
              {t({
                id: "cx-actions-page.new-automation",
                defaultMessage: "New Automation",
              })}
            </span>
          </div>
        ),
      }}
    >
      <BottomCenteredDrawer.Content className="overflow-y-scroll px-8 pt-8">
        <div className="flex justify-center pb-8">
          <div className="flex w-full max-w-[1200px] flex-col gap-6">
            <div className="flex items-center gap-2">
              <APIIcon />
              <span className="font-secondary text-xl font-medium text-black">
                {t({
                  id: "cx-actions-page.custom-api-action",
                  defaultMessage: "Custom API Action",
                })}
              </span>
            </div>
            <div className="flex flex-col gap-10">
              <CXAutomationGeneralSection
                value={state}
                onChange={patch}
                isOpen={openSection === "general"}
                onOpenChange={generalOpenChange}
                onProceed={() => setOpenSection("apiSetup")}
              />
              <CXAutomationApiSetupSection
                value={state}
                onChange={patch}
                isOpen={openSection === "apiSetup"}
                onOpenChange={apiSetupOpenChange}
                onProceed={() => setOpenSection("testResponse")}
              />
              <CXAutomationTestResponseSection
                isOpen={openSection === "testResponse"}
                onOpenChange={testResponseOpenChange}
                dataInputs={state.dataInputs}
                tokenValues={tokenValues}
                onTokenValuesChange={setTokenValues}
                onTest={handleTest}
                isTestDisabled={isTestDisabled}
                isTestRunning={isTestRunning}
                testResponse={testResponse}
                onSave={handleSubmit}
                isSaveDisabled={isSaveDisabled}
                isSaving={isSaving}
              />
            </div>
          </div>
        </div>
      </BottomCenteredDrawer.Content>
    </BottomCenteredDrawer>
  );
};
